import { useState, useRef, useCallback } from 'react';
import { useEditorStore } from '../../../stores/editor.js';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

/**
 * POST /api/v1/edit/{sessionId}/finish — the terminal transition that
 * unblocks the MCP poller waiting on agentbrush_edit_mask_result.
 *
 * The session record is the auth (same trust model as /accept and /submit),
 * so no auth headers required. Server requires Idempotency-Key + an empty
 * JSON body; we mint a fresh UUID per click.
 *
 * On success we flip the local sessionFinished flag — the editor then
 * locks all inputs and shows a "delivered" state.
 */
export function useFinishSession(sessionId: string | undefined) {
  const [isFinishing, setIsFinishing] = useState(false);
  const inFlightRef = useRef(false);
  const markSessionFinished = useEditorStore((s) => s.markSessionFinished);
  const setSubmissionError = useEditorStore((s) => s.setSubmissionError);

  const finish = useCallback(async (): Promise<boolean> => {
    if (!sessionId || inFlightRef.current) return false;
    inFlightRef.current = true;
    setIsFinishing(true);
    setSubmissionError(null);

    const idempotencyKey = crypto.randomUUID();

    try {
      const res = await fetch(`${API_BASE}/v1/edit/${sessionId}/finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({}),
      });

      if (res.ok) {
        markSessionFinished();
        return true;
      }

      const errBody = (await res.json().catch(() => ({}))) as {
        error?: { message?: string };
      };
      setSubmissionError(errBody.error?.message ?? 'Failed to finish the session.');
      return false;
    } catch {
      setSubmissionError('Network error while finishing the session.');
      return false;
    } finally {
      inFlightRef.current = false;
      setIsFinishing(false);
    }
  }, [sessionId, markSessionFinished, setSubmissionError]);

  return { finish, isFinishing };
}
